import {
  Button,
  Card,
  Icon,
  IntentBadge,
  type Intent,
} from "@excellent/ui";
import type { EvidenceTaskSummaryDto } from "@excellent/ui/api/contracts/evidence";

const NUM = "tabular-nums";

type FocusItem = {
  key: string;
  label: string;
  value: string;
  detail: string;
  intent: Intent;
  icon: string;
  actionLabel: string;
  target: EvidenceTaskSummaryDto | null;
};

function needsProof(task: EvidenceTaskSummaryDto): boolean {
  return !task.hasEvidenceArtifact || task.claimCount === 0 || task.receiptCount === 0;
}

export function VerificationQueueFocus({
  tasks,
  onOpen,
}: {
  tasks: EvidenceTaskSummaryDto[];
  onOpen: (id: number) => void;
}) {
  const reviewTasks = tasks.filter((task) => task.status === "review");
  const proofGaps = tasks.filter(needsProof);
  const packReady = tasks.filter((task) => task.hasBundle);
  const acceptedWithEvidence = tasks.filter(
    (task) => task.status === "done" && task.hasEvidenceArtifact,
  );
  const firstReview = reviewTasks[0] ?? null;
  const firstProofGap = proofGaps[0] ?? null;
  const firstPack = packReady[0] ?? null;
  const firstAccepted = acceptedWithEvidence[0] ?? null;

  const items: FocusItem[] = [
    {
      key: "review",
      label: "Verifier queue",
      value: `${reviewTasks.length} waiting`,
      detail: firstReview
        ? `Next: #${firstReview.id} ${firstReview.title}`
        : "No run is waiting on a verifier decision.",
      intent: reviewTasks.length > 0 ? "warning" : "success",
      icon: "shield-check",
      actionLabel: "Open next",
      target: firstReview,
    },
    {
      key: "proof",
      label: "Proof gaps",
      value: `${proofGaps.length} incomplete`,
      detail: firstProofGap
        ? `First gap: #${firstProofGap.id} needs claims, receipts, or artifact output.`
        : "Visible runs have the core proof fields needed for replay.",
      intent: proofGaps.length > 0 ? "warning" : "success",
      icon: "alert-triangle",
      actionLabel: "Inspect gap",
      target: firstProofGap,
    },
    {
      key: "packs",
      label: "Replay packs",
      value: `${packReady.length} ready`,
      detail: firstPack
        ? `Open #${firstPack.id} to download or replay the evidence pack.`
        : "No composable replay pack is visible yet.",
      intent: packReady.length > 0 ? "success" : "warning",
      icon: "package",
      actionLabel: "Open pack",
      target: firstPack,
    },
    {
      key: "accepted",
      label: "Accepted proof",
      value: `${acceptedWithEvidence.length} accepted`,
      detail: firstAccepted
        ? `Latest accepted run in view: #${firstAccepted.id}.`
        : "Accepted buyer-ready evidence will surface here.",
      intent: acceptedWithEvidence.length > 0 ? "success" : "neutral",
      icon: "check-circle",
      actionLabel: "Open proof",
      target: firstAccepted,
    },
  ];

  return (
    <Card
      aria-label="Verification queue focus"
      className="gap-0 overflow-hidden py-0"
    >
      <div className="grid gap-px bg-border md:grid-cols-2 xl:grid-cols-4">
        {items.map((item) => (
          <div key={item.key} className="flex min-h-[9.5rem] flex-col bg-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex size-8 items-center justify-center rounded-md border border-border bg-background text-muted-foreground">
                  <Icon name={item.icon} size="sm" />
                </span>
                <div>
                  <div className="type-subtext font-medium text-muted-foreground">
                    {item.label}
                  </div>
                  <div className={`${NUM} type-body font-medium text-foreground`}>
                    {item.value}
                  </div>
                </div>
              </div>
              <IntentBadge intent={item.intent}>{item.intent}</IntentBadge>
            </div>
            <p className="mt-3 line-clamp-3 type-subtext text-muted-foreground">
              {item.detail}
            </p>
            <div className="mt-auto pt-3">
              <Button
                size="sm"
                variant="outline"
                disabled={!item.target}
                onClick={() => {
                  if (item.target) onOpen(item.target.id);
                }}
              >
                <Icon name="arrow-right" size="sm" />
                {item.actionLabel}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
